import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import AppImage from '../../../components/AppImage';

const AttendanceReportTable = ({ data }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const filteredData = data?.filter(log => {
    const matchesSearch = log?.user_profiles?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         log?.user_profiles?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterType === 'all' || log?.entry_type === filterType;
    return matchesSearch && matchesFilter;
  });

  if (!data || data?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Attendance Records</h2>
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="FileText" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No attendance records available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h2 className="text-lg font-semibold text-foreground">Attendance Records</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full sm:w-64"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            <option value="all">All Types</option>
            <option value="barcode_scan">Barcode Scan</option>
            <option value="manual">Manual</option>
            <option value="override">Override</option>
          </select>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Member</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Check In</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Check Out</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Duration</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Type</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredData?.map((log) => (
              <tr key={log?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <AppImage
                      src={log?.user_profiles?.avatar_url || '/assets/images/no_image.png'}
                      alt={log?.user_profiles?.full_name || 'Member'}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {log?.user_profiles?.full_name || 'Unknown'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {log?.user_profiles?.email}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <p className="text-sm text-foreground">
                    {format(new Date(log?.check_in_time), 'MMM dd, yyyy')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(log?.check_in_time), 'hh:mm a')}
                  </p>
                </td>
                <td className="py-3 px-4">
                  {log?.check_out_time ? (
                    <>
                      <p className="text-sm text-foreground">
                        {format(new Date(log?.check_out_time), 'MMM dd, yyyy')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(log?.check_out_time), 'hh:mm a')}
                      </p>
                    </>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-foreground">
                    {log?.duration_minutes ? `${log?.duration_minutes} min` : '-'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs font-medium text-foreground capitalize">
                    {log?.entry_type?.replace('_', ' ')}
                  </span>
                </td>
                <td className="py-3 px-4">
                  {log?.check_out_time ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/10 text-success text-xs font-medium">
                      <Icon name="CheckCircle" size={12} />
                      Completed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-warning/10 text-warning text-xs font-medium">
                      <Icon name="Clock" size={12} />
                      Active
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {filteredData?.map((log) => (
          <div key={log?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <AppImage
                src={log?.user_profiles?.avatar_url || '/assets/images/no_image.png'}
                alt={log?.user_profiles?.full_name || 'Member'}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  {log?.user_profiles?.full_name || 'Unknown'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {log?.user_profiles?.email}
                </p>
              </div>
              {log?.check_out_time ? (
                <Icon name="CheckCircle" size={20} className="text-success" />
              ) : (
                <Icon name="Clock" size={20} className="text-warning" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs mb-1">Check In</p>
                <p className="text-foreground font-medium">
                  {format(new Date(log?.check_in_time), 'MMM dd, hh:mm a')}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Duration</p>
                <p className="text-foreground font-medium">
                  {log?.duration_minutes ? `${log?.duration_minutes} min` : 'Active'}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredData?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No records match your search criteria</p>
        </div>
      )}
    </div>
  );
};

export default AttendanceReportTable;